import {
	format,
	parseISO,
	differenceInMilliseconds,
	differenceInDays,
	differenceInHours,
	differenceInMinutes,
	differenceInSeconds,
	isBefore,
	isAfter,
	isToday as dateFnsIsToday,
	startOfDay as dateFnsStartOfDay,
	endOfDay as dateFnsEndOfDay,
	startOfWeek as dateFnsStartOfWeek,
	endOfWeek as dateFnsEndOfWeek,
	startOfMonth as dateFnsStartOfMonth,
	endOfMonth as dateFnsEndOfMonth,
	startOfYear as dateFnsStartOfYear,
	endOfYear as dateFnsEndOfYear,
	setHours,
	setMinutes,
	setSeconds,
	setMilliseconds,
	getDay,
} from 'date-fns';
import { toZonedTime, fromZonedTime, format as formatTz } from 'date-fns-tz';
import { instanceToInstance } from 'class-transformer';
import { ObjectMap } from './ObjectMap';

function toDate(date: Date | string | number): Date {
	if (date instanceof Date) return date;
	if (typeof date === 'number') return new Date(date);
	return parseISO(date as string);
}

export class Utilities {

	static englishJoinArray(array: any[]): string {
		let outStr = '';

		if (array.length === 1) {
			outStr = array[0];

		} else if (array.length === 2) {
			outStr = array.join(' and ');

		} else if (array.length > 2) {
			outStr = array.slice(0, -1).join(', ') + ', and ' + array.slice(-1);
		}
		return outStr;
	}

	/**
	* Serializes an object including getter properties, excluding private props (starting with '_').
	* Walks the prototype chain to find all getters.
	*/
	static serializeWithGetters(obj: any): any {
		if (obj === null || obj === undefined) return obj;
		if (typeof obj !== 'object') return obj;
		if (Array.isArray(obj)) return obj.map(item => this.serializeWithGetters(item));

		const result: any = {};

		// Get own enumerable properties (excluding private)
		for (const key of Object.keys(obj)) {
			if (!key.startsWith('_')) {
				result[key] = this.serializeWithGetters(obj[key]);
			}
		}

		// Get getter properties from the prototype chain
		let proto = Object.getPrototypeOf(obj);
		while (proto && proto !== Object.prototype) {
			const descriptors = Object.getOwnPropertyDescriptors(proto);
				for (const [key, descriptor] of Object.entries(descriptors)) {
					if (descriptor.get && !key.startsWith('_') && !(key in result)) {
					try {
						result[key] = this.serializeWithGetters(obj[key]);
					} catch {
						// Skip if getter throws
					}
				}
			}
			proto = Object.getPrototypeOf(proto);
		}
		return result;
	}

	/**
	* Deep clones a value, preserving type information for class instances.
	* Uses class-transformer's instanceToInstance for class instances (preserving prototype chain
	* and @Expose() decorated properties), and structuredClone for plain objects and primitives.
	* @param obj - The value to clone
	* @returns A deep clone of the value with the original type preserved
	*/
	static deepCloneObject<T>(obj: T): T {
		if (obj === null || obj === undefined) return obj;
		const proto = Object.getPrototypeOf(obj);
		if (proto !== null && proto !== Object.prototype && typeof obj === 'object' && !Array.isArray(obj)) {
			return instanceToInstance(obj);
		}
		return structuredClone(obj);
	}

	/**
	* Recursively walk through an object, looking for properties with names that contain a substring.
	* @param obj - The object to walk through
	* @param substring - The substring to search for (case-insensitive)
	* @return {Object} - The collection of found properties
	*/
	static findPropertyByNameContains(obj: any, substring: string, _visited = new WeakSet()): any {
	const result: any = {};
		const substring_lower = substring.toLowerCase();

		if (_visited.has(obj)) {
			return result;
		}

		_visited.add(obj);

		if (obj === null || obj === undefined) {
		return result;
	}

	for (const key in obj) {
		if (obj.hasOwnProperty(key) && typeof obj[key] === 'object' && !_visited.has(obj[key])) {
			if (key.toLowerCase().indexOf(substring_lower) > -1) {
				result[key] = obj[key];
			}

			const nestedResult: ObjectMap = this.findPropertyByNameContains(obj[key], substring, _visited);
		for (const nestedKey in nestedResult) {
			if (nestedResult.hasOwnProperty(nestedKey)) {
				result[nestedKey] = nestedResult[nestedKey];
			}
		}
	}
}

	return result;
}

	/**
	* Recursively walks through an object looking for properties by name, returns an array of matching property names (including path to property).
	*/
	static findPropertyByNameContainsPath(obj: any, substring: string, _visited = new WeakSet(), _path = ''): string[] {
	const pathArray: string[] = [];
		const substring_lower = substring.toLowerCase();

		if (_visited.has(obj)) {
			return pathArray;
	}

	_visited.add(obj);

if (obj === null || obj === undefined) {
		return pathArray;
	}

for (const key in obj) {
		if (obj.hasOwnProperty(key) && typeof obj[key] === 'object' && !_visited.has(obj[key])) {
			const currentPath = _path ? `${_path}.${key}` : key;

		if (key.toLowerCase().indexOf(substring_lower) > -1) {
			pathArray.push(currentPath);
		}

		const nestedPathArray: string[] = this.findPropertyByNameContainsPath(obj[key], substring, _visited, currentPath);
		for (const nestedPath of nestedPathArray) {
			pathArray.push(nestedPath);
	}
}
}

	return pathArray;
}

	/**
	* Returns a new array without a specific value
	*/
	static arrayRemove(array: any[], value: any): any[] {
		return array.filter(item => item !== value);
}

	/**
	* Return true if str is null, undefined, or empty string
	*/
	static isNullOrEmpty(str: any): boolean {
		return str === null || str === undefined || str === '';
}

	/**
	* Return true if str is an empty string, false otherwise
	*/
	static isEmptyString(str: string): boolean {
		return str === '';
}

	/**
	* Return true if array is null, undefined, or empty
	*/
	static isNullOrEmptyArray(array: any[]): boolean {
		return array === null || array === undefined || array.length === 0;
}

	/**
	* Returns the number of milliseconds since the UNIX epoch for the passed in date
	*/
	static dateToMillis(date: Date | string | number): number {
		return toDate(date).getTime();
}

	/**
	* Converts milliseconds to a Date object
	*/
	static millisToDate(ms: number): Date {
		return new Date(ms);
}

	/**
	* Returns the number of milliseconds since the UNIX epoch for the passed in date (timezone aware)
	*/
	static dateToMillisTz(date: Date | string | number, tz: string): number {
		return fromZonedTime(toDate(date), tz).getTime();
}

	/**
	* Returns date in a human readable format. Example: Saturday, January 1, 2011
	*/
	static dateToString(date: Date | string | number, tz?: string): string {
		const d = toDate(date);
		if (tz) {
			return formatTz(toZonedTime(d, tz), 'EEEE, MMMM d, yyyy', { timeZone: tz });
		}
		return format(d, 'EEEE, MMMM d, yyyy');
}

	/**
	* Returns date in US long format. Example: Mon, Jan 1, 2011
	*/
	static dateToStringShort(date: Date | string | number, tz?: string): string {
		const d = toDate(date);
		if (tz) {
			return formatTz(toZonedTime(d, tz), 'EEE, MMM d, yyyy', { timeZone: tz });
		}
		return format(d, 'EEE, MMM d, yyyy');
}

	/**
	* Returns date in US medium format. Example: Jan 1, 2011
	*/
	static dateToStringMedium(date: Date | string | number, tz?: string): string {
		const d = toDate(date);
		if (tz) {
			return formatTz(toZonedTime(d, tz), 'MMM d, yyyy', { timeZone: tz });
		}
		return format(d, 'MMM d, yyyy');
}

	/**
	* Returns date in US short format. Example: 1/1/2011
	*/
	static dateToStringUS(date: Date | string | number, tz?: string): string {
		const d = toDate(date);
		if (tz) {
			return formatTz(toZonedTime(d, tz), 'M/d/yyyy', { timeZone: tz });
		}
		return format(d, 'M/d/yyyy');
}

	/**
	* Returns time in 12-hour format. Example: 2:30 PM
	*/
	static timeToString(date: Date | string | number, tz?: string): string {
		const d = toDate(date);
		if (tz) {
			return formatTz(toZonedTime(d, tz), 'h:mm a', { timeZone: tz });
		}
		return format(d, 'h:mm a');
}

	/**
	* Returns time in 24-hour format. Example: 14:30
	*/
	static timeToStringMilitary(date: Date | string | number, tz?: string): string {
		const d = toDate(date);
		if (tz) {
			return formatTz(toZonedTime(d, tz), 'HH:mm', { timeZone: tz });
		}
		return format(d, 'HH:mm');
}

	/**
	* Returns time and date in 12-hour format. Example: 2:30 PM on Saturday, January 1, 2011
	*/
	static dateTimeToString(date: Date | string | number, tz?: string): string {
		const d = toDate(date);
		if (tz) {
			return formatTz(toZonedTime(d, tz), "h:mm a 'on' EEEE, MMMM d, yyyy", { timeZone: tz });
		}
		return format(d, "h:mm a 'on' EEEE, MMMM d, yyyy");
}

	/**
	* Returns time and date in 12-hour format. Example: 2:30 PM on Sat, Jan 1, 2011
	*/
	static dateTimeToStringShort(date: Date | string | number, tz?: string): string {
		const d = toDate(date);
		if (tz) {
			return formatTz(toZonedTime(d, tz), "h:mm a 'on' EEE, MMM d, yyyy", { timeZone: tz });
		}
		return format(d, "h:mm a 'on' EEE, MMM d, yyyy");
}

	/**
	* Returns time and date in 12-hour format. Example: 2:30 PM, Jan 1, 2011
	*/
	static dateTimeToStringMedium(date: Date | string | number, tz?: string): string {
		const d = toDate(date);
		if (tz) {
			return formatTz(toZonedTime(d, tz), 'h:mm a, MMM d, yyyy', { timeZone: tz });
		}
		return format(d, 'h:mm a, MMM d, yyyy');
}

	/**
	* Returns time and date in US short format. Example: 2:30 PM, 1/1/2011
	*/
	static dateTimeToStringUS(date: Date | string | number, tz?: string): string {
		const d = toDate(date);
		if (tz) {
			return formatTz(toZonedTime(d, tz), 'h:mm a, M/d/yyyy', { timeZone: tz });
		}
		return format(d, 'h:mm a, M/d/yyyy');
}

	/**
	* Returns ISO 8601 formatted string (YYYY-MM-DD)
	*/
	static dateToIso(date: Date | string | number): string {
		return format(toDate(date), 'yyyy-MM-dd');
}

	/**
	* Returns ISO 8601 formatted string (YYYY-MM-DDTHH:mm:ss.SSSxxx)
	*/
	static dateTimeToIso(date: Date | string | number): string {
		return toDate(date).toISOString();
}

	/**
	* Returns milliseconds as HH:MM:SS format
	*/
	static millisToDuration(ms: number): string {
		const totalSeconds = Math.floor(ms / 1000);
		const hours = Math.floor(totalSeconds / 3600);
		const minutes = Math.floor((totalSeconds % 3600) / 60);
		const seconds = totalSeconds % 60;
		return `${this.padNumber(hours, 2)}:${this.padNumber(minutes, 2)}:${this.padNumber(seconds, 2)}`;
}

	/**
	* Pads a number with leading zeros
	*/
	static padNumber(n: number, width: number): string {
		const s = n + '';
		return s.length >= width ? s : new Array(width - s.length + 1).join('0') + s;
}

	/**
	* Returns true if the provided value is numeric
	*/
	static isNumeric(value: any): boolean {
		return !isNaN(value) && !isNaN(parseFloat(value));
}

	/**
	* Returns the number of milliseconds until the next day change at a given timezone
	*/
	static msUntilNextDayChange(tz: string): number {
		const now = new Date();
		const nowZoned = toZonedTime(now, tz);
		const nextDayZoned = dateFnsStartOfDay(new Date(nowZoned.getTime() + 24 * 60 * 60 * 1000));
		const nextDayUtc = fromZonedTime(nextDayZoned, tz);
		return nextDayUtc.getTime() - now.getTime();
}

	/**
	* Returns the number of milliseconds until a specific time on the next occurrence of a given day of the week
	*/
	static msUntilNextDayOfWeek(dayOfWeek: number, tz: string, time?: string): number {
		const now = new Date();
		const nowZoned = toZonedTime(now, tz);
		let targetZoned = nowZoned;

		// Advance to the target day of week
		const currentDay = getDay(nowZoned);
		let daysUntil = dayOfWeek - currentDay;
		if (daysUntil <= 0) daysUntil += 7;
		targetZoned = new Date(nowZoned.getTime() + daysUntil * 24 * 60 * 60 * 1000);

		if (time) {
			const [h, m] = time.split(':').map(Number);
			targetZoned = setMilliseconds(setSeconds(setMinutes(setHours(targetZoned, h), m), 0), 0);
		}

		const targetUtc = fromZonedTime(targetZoned, tz);
		return targetUtc.getTime() - now.getTime();
}

	/**
	* Returns milliseconds until the next day change. Timezone defaults to 'America/New_York'
	*/
	static msUntilNextDayChangeDefault(tz: string = 'America/New_York'): number {
		return this.msUntilNextDayChange(tz);
}

	/**
	* Parses a time string in HH:MM:SS format and returns milliseconds
	*/
	static timeStringToMillis(timeString: string): number {
		const parts = timeString.split(':');
		let ms = 0;

		if (parts.length > 0) ms += parseInt(parts[0]) * 3600000; // hours
		if (parts.length > 1) ms += parseInt(parts[1]) * 60000; // minutes
		if (parts.length > 2) ms += parseInt(parts[2]) * 1000; // seconds

		return ms;
}

	/**
	* Returns true if a date is between two other dates (inclusive)
	*/
	static isBetween(date: Date | string | number, start: Date | string | number, end: Date | string | number): boolean {
		const d = toDate(date).getTime();
		const s = toDate(start).getTime();
		const e = toDate(end).getTime();
		return d >= s && d <= e;
}

	/**
	* Returns the difference between two dates in milliseconds
	*/
	static diffInMillis(date1: Date | string | number, date2: Date | string | number): number {
		return differenceInMilliseconds(toDate(date1), toDate(date2));
}

	/**
	* Returns the difference between two dates in days
	*/
	static diffInDays(date1: Date | string | number, date2: Date | string | number): number {
		return differenceInDays(toDate(date1), toDate(date2));
}

	/**
	* Returns the difference between two dates in hours
	*/
	static diffInHours(date1: Date | string | number, date2: Date | string | number): number {
		return differenceInHours(toDate(date1), toDate(date2));
}

	/**
	* Returns the difference between two dates in minutes
	*/
	static diffInMinutes(date1: Date | string | number, date2: Date | string | number): number {
		return differenceInMinutes(toDate(date1), toDate(date2));
}

	/**
	* Returns the difference between two dates in seconds
	*/
	static diffInSeconds(date1: Date | string | number, date2: Date | string | number): number {
		return differenceInSeconds(toDate(date1), toDate(date2));
}

	/**
	* Returns true if a date is in the past
	*/
	static isPast(date: Date | string | number): boolean {
		return isBefore(toDate(date), new Date());
}

	/**
	* Returns true if a date is in the future
	*/
	static isFuture(date: Date | string | number): boolean {
		return isAfter(toDate(date), new Date());
}

	/**
	* Returns true if a date is today
	*/
	static isToday(date: Date | string | number): boolean {
		return dateFnsIsToday(toDate(date));
}

	/**
	* Returns the start of day for a given date (timezone aware)
	*/
	static startOfDay(date: Date | string | number, tz?: string): Date {
		const d = toDate(date);
		if (tz) {
			return fromZonedTime(dateFnsStartOfDay(toZonedTime(d, tz)), tz);
		}
		return dateFnsStartOfDay(d);
}

	/**
	* Returns the end of day for a given date (timezone aware)
	*/
	static endOfDay(date: Date | string | number, tz?: string): Date {
		const d = toDate(date);
		if (tz) {
			return fromZonedTime(dateFnsEndOfDay(toZonedTime(d, tz)), tz);
		}
		return dateFnsEndOfDay(d);
}

	/**
	* Returns the start of week for a given date
	*/
	static startOfWeek(date: Date | string | number, tz?: string): Date {
		const d = toDate(date);
		if (tz) {
			return fromZonedTime(dateFnsStartOfWeek(toZonedTime(d, tz)), tz);
		}
		return dateFnsStartOfWeek(d);
}

	/**
	* Returns the end of week for a given date
	*/
	static endOfWeek(date: Date | string | number, tz?: string): Date {
		const d = toDate(date);
		if (tz) {
			return fromZonedTime(dateFnsEndOfWeek(toZonedTime(d, tz)), tz);
		}
		return dateFnsEndOfWeek(d);
}

	/**
	* Returns the start of month for a given date
	*/
	static startOfMonth(date: Date | string | number, tz?: string): Date {
		const d = toDate(date);
		if (tz) {
			return fromZonedTime(dateFnsStartOfMonth(toZonedTime(d, tz)), tz);
		}
		return dateFnsStartOfMonth(d);
}

	/**
	* Returns the end of month for a given date
	*/
	static endOfMonth(date: Date | string | number, tz?: string): Date {
		const d = toDate(date);
		if (tz) {
			return fromZonedTime(dateFnsEndOfMonth(toZonedTime(d, tz)), tz);
		}
		return dateFnsEndOfMonth(d);
}

	/**
	* Returns the start of year for a given date
	*/
	static startOfYear(date: Date | string | number, tz?: string): Date {
		const d = toDate(date);
		if (tz) {
			return fromZonedTime(dateFnsStartOfYear(toZonedTime(d, tz)), tz);
		}
		return dateFnsStartOfYear(d);
}

	/**
	* Returns the end of year for a given date
	*/
	static endOfYear(date: Date | string | number, tz?: string): Date {
		const d = toDate(date);
		if (tz) {
			return fromZonedTime(dateFnsEndOfYear(toZonedTime(d, tz)), tz);
		}
		return dateFnsEndOfYear(d);
}

	/**
	* Returns the number of seconds until a certain date
	*/
	static secondsUntilDate(date: Date | string | number): number {
		return Math.max(0, differenceInSeconds(toDate(date), new Date()));
}

	/**
	* Returns the number of milliseconds until a certain date
	*/
	static millisUntilDate(date: Date | string | number): number {
		return Math.max(0, differenceInMilliseconds(toDate(date), new Date()));
}

	/**
	* Recursively adds the property and its value to the returned object starting with property names that match
	*/
	static extractObjects(obj: any[], propertyName: string): any[] {
		const result: any[] = [];

		for (let i = 0; i < obj.length; i++) {
			if (obj[i].hasOwnProperty(propertyName)) {
				result.push(obj[i][propertyName]);
			}
		}

		return result;
}

	/**
	* Removes and returns the first element of an array
	*/
	static arrayShift(array: any[]): any {
		return array.shift();
}

	/**
	* Removes and returns the last element of an array
	*/
	static arrayPop(array: any[]): any {
		return array.pop();
}

	/**
	* Combines two objects into one
	*/
	static objectMerge(obj1: any, obj2: any): any {
		return Object.assign({}, obj1, obj2);
}

	/**
	* Returns a random element from an array
	*/
	static arrayRandom(array: any[]): any {
		return array[Math.floor(Math.random() * array.length)];
}

	/**
	* Returns a random integer between a and b
	*/
	static randomInteger(a: number, b: number): number {
		return Math.floor(Math.random() * (b - a + 1) + a);
}

	/**
	* Returns an array of unique values from an array
	*/
	static arrayUnique(array: any[]): any[] {
		return [...new Set(array)];
}

	/**
	* Returns true if array contains object
	*/
	static arrayContains(array: any[], obj: any): boolean {
		return array.indexOf(obj) > -1;
}

	/**
	* Returns true if all elements of array are contained in compareArray
	*/
	static arrayContainsAll(array: any[], compareArray: any[]): boolean {
		let allContains = true;

		for (let i = 0; i < array.length; i++) {
			if (compareArray.indexOf(array[i]) === -1) {
				allContains = false;
			}
		}

	return allContains;
}

	/**
	* Returns true if some elements of array are contained in compareArray
	*/
	static arrayContainsSome(array: any[], compareArray: any[]): boolean {
		let someContains = false;

		for (let i = 0; i < array.length; i++) {
			if (compareArray.indexOf(array[i]) > -1) {
				someContains = true;
			}
		}

	return someContains;
}

	/**
	* Returns true if array is sorted
	*/
	static isArraySorted(array: any[]): boolean {
		for (let i = 1; i < array.length; i++) {
			if (array[i] < array[i - 1]) {
				return false;
			}
		}
		return true;
}

	/**
	* Returns true if string starts with substring
	*/
	static stringStartsWith(str: string, substring: string): boolean {
		return str.indexOf(substring) === 0;
}

	/**
	* Returns true if string ends with substring
	*/
	static stringEndsWith(str: string, substring: string): boolean {
		return str.lastIndexOf(substring) === str.length - substring.length;
}

	/**
	* Returns true if string contains substring
	*/
	static stringContains(str: string, substring: string): boolean {
		return str.indexOf(substring) > -1;
}

	/**
	* Returns a new string with all occurrences of search replaced with replacement
	*/
	static stringReplace(str: string, search: string, replacement: string): string {
		return str.split(search).join(replacement);
}

	/**
	* Returns true if string contains substring (case-insensitive)
	*/
	static stringContainsCaseInsensitive(str: string, substring: string): boolean {
		return str.toLowerCase().indexOf(substring.toLowerCase()) > -1;
}

	/**
	* Returns a GUID
	*/
	static createGUID(): string {
		const S4 = () => (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
		return `${S4()}${S4()}-${S4()}-${S4()}-${S4()}-${S4()}${S4()}${S4()}`;
}

	/**
	* Returns a UUID
	*/
	static createUUID(): string {
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
			const r = Math.random() * 16 | 0,
				v = c === 'x' ? r : (r & 0x3 | 0x8);
			return v.toString(16);
		});
}

	/**
	* Returns a camelCase string from a snake_case string
	*/
	static snakeToCamel(str: string): string {
		return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
			if (+index === 0) {
				return match.toLowerCase();
			}
			return match.toUpperCase();
		});
}

	/**
	* Returns a snake_case string from a camelCase string
	*/
	static camelToSnake(str: string): string {
		return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

	/**
	* Returns a kebab-case string from a camelCase string
	*/
	static camelToKebab(str: string): string {
		return str.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
}

	/**
	* Returns a PascalCase string from a camelCase string
	*/
	static camelToPascal(str: string): string {
		return str.charAt(0).toUpperCase() + str.slice(1);
}

	/**
	* Returns a camelCase string from a PascalCase string
	*/
	static pascalToCamel(str: string): string {
		return str.charAt(0).toLowerCase() + str.slice(1);
}

	/**
	* Returns a PascalCase string from a kebab-case string
	*/
	static kebabToPascal(str: string): string {
		return str.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('');
}

	/**
	* Returns a camelCase string from a kebab-case string
	*/
	static kebabToCamel(str: string): string {
		return this.pascalToCamel(this.kebabToPascal(str));
}

	/**
	* Returns a kebab-case string from a PascalCase string
	*/
	static pascalToKebab(str: string): string {
		return str.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
}

	/**
	* Returns a kebab-case string from a snake_case string
	*/
	static snakeToKebab(str: string): string {
		return str.replace(/_/g, '-');
}

	/**
	* Returns a snake_case string from a kebab-case string
	*/
	static kebabToSnake(str: string): string {
		return str.replace(/-/g, '_');
}

	/**
	* Returns a camelCase string from a snake_case string
	*/
	static snakeToCamelStandard(str: string): string {
		return str.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
}

	/**
	* Returns a pascal_case_string from an object
	*/
	static objectToString(obj: any): string {
		let outStr = '';

		for (const key in obj) {
			if (obj.hasOwnProperty(key)) {
				outStr += key.toUpperCase() + ': ' + obj[key].toString() + ', ';
			}
		}

	return outStr.substring(0, outStr.length - 2);
}

	/**
	* Returns true if string is GUID
	*/
	static isGUID(str: string): boolean {
		const guid = /^(\{)?[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}(\})?$/i;
		return guid.test(str);
}

	/**
	* Returns true if string is UUID
	*/
	static isUUID(str: string): boolean {
		const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;
		return uuid.test(str);
}

	/**
	* Returns true if object is empty ({})
	*/
	static objectIsEmpty(obj: any): boolean {
		for (const key in obj) {
			if (obj.hasOwnProperty(key)) {
				return false;
			}
		}
		return true;
}

	/**
	* Returns a flattened copy of a nested array
	*/
	static arrayFlatten(array: any[]): any[] {
		return array.reduce((flat: any[], toFlatten: any) => {
			return flat.concat(Array.isArray(toFlatten) ? this.arrayFlatten(toFlatten) : toFlatten);
		}, []);
}

	/**
	* Returns the sum of all values in an array
	*/
	static arraySum(array: any[]): number {
		return array.reduce((sum, val) => sum + (Number(val) || 0), 0);
}

	/**
	* Returns the product of all values in an array
	*/
	static arrayProduct(array: any[]): number {
		return array.reduce((product, val) => product * (Number(val) || 1), 1);
}

	/**
	* Returns the average of all values in an array
	*/
	static arrayAverage(array: any[]): number {
		const sum = this.arraySum(array);
		const average = sum / array.length;
		return average;
}

	/**
	* Returns the minimum value of all values in an array
	*/
	static arrayMin(array: any[]): number {
		return Math.min(...array);
}

	/**
	* Returns the maximum value of all values in an array
	*/
	static arrayMax(array: any[]): number {
		return Math.max(...array);
}

	/**
	* Returns a new string with leading zeros added
	*/
	static padLeft(str: string, totalWidth: number, paddingChar: string): string {
		return (paddingChar.repeat(totalWidth) + str).slice(-totalWidth);
}

	/**
	* Returns a new string with trailing spaces added
	*/
	static padRight(str: string, totalWidth: number, paddingChar: string): string {
		return (str + paddingChar.repeat(totalWidth)).substring(0, totalWidth);
}

	/**
	* Returns a boolean telling if string matches regex pattern
	*/
	static regexTest(pattern: string, str: string): boolean {
		const regex = new RegExp(pattern);
		return regex.test(str);
}

	/**
	* Returns an array of all strings that match the regex pattern
	*/
	static regexMatch(pattern: string, str: string): any[] {
		const regex = new RegExp(pattern, 'g');
		return str.match(regex) || [];
}

	/**
	* Returns a new string where all matches are replaced with the specified replacement string
	*/
	static regexReplace(pattern: string, str: string, replacement: string): string {
		const regex = new RegExp(pattern, 'g');
		return str.replace(regex, replacement);
}

	/**
	* Returns the result of split using a regex pattern
	*/
	static regexSplit(pattern: string, str: string): string[] {
		const regex = new RegExp(pattern);
		return str.split(regex);
}

	/**
	* Returns a boolean telling if string is a valid email format
	*/
	static isEmail(str: string): boolean {
		const email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return email.test(str);
}

	/**
	* Returns a boolean telling if string is a valid HTTP URL format
	*/
	static isHttpUrl(str: string): boolean {
		try {
			new URL(str);
			return (str.startsWith('http://') || str.startsWith('https://'));
		} catch (e) {
			return false;
		}
}

	/**
	* Returns a boolean telling if string is a valid URL format
	*/
	static isUrl(str: string): boolean {
		try {
			new URL(str);
			return true;
		} catch (e) {
			return false;
		}
}

	/**
	* Returns an array with a single copy of each unique element
	*/
	static uniqueArrayByKey(array: any[], key: string): any[] {
		const seen: any = {};
		return array.filter(item => {
			if (seen[item[key]]) {
				return false;
			}
			seen[item[key]] = true;
			return true;
		});
}

	/**
	* Returns an array sorted by the given key in ascending order
	*/
	static sortByKey(array: any[], key: string): any[] {
		return array.sort((a, b) => {
			if (a[key] < b[key]) {
				return -1;
			}
			if (a[key] > b[key]) {
				return 1;
			}
			return 0;
		});
}

	/**
	* Returns an array sorted by the given key in descending order
	*/
	static sortByKeyDesc(array: any[], key: string): any[] {
		return array.sort((a, b) => {
			if (a[key] > b[key]) {
				return -1;
			}
			if (a[key] < b[key]) {
				return 1;
			}
			return 0;
		});
}

	/**
	* Returns an array of values from specified key from array of objects
	*/
	static map(array: any[], mapper: (item: any) => any): any[] {
		return array.map(mapper);
}

	/**
	* Returns a number representing the percentage calculated from numerator and denominator
	*/
	static calcPercentage(numerator: number, denominator: number): number {
		if (denominator === 0) {
			return 0;
		}
		return (numerator / denominator) * 100;
}

	/**
	* Returns a string representation of bytes in a human readable format
	*/
	static formatBytes(bytes: number, decimals: number = 2): string {
		if (bytes === 0) {
			return '0 Bytes';
		}

		const k = 1024;
		const dm = decimals < 0 ? 0 : decimals;
		const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));

		return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

	/**
	* Returns a hash of the input string using a simple hashing algorithm
	*/
	static hashString(str: string): string {
		let hash = 0;
		if (str.length === 0) {
			return hash.toString();
		}

		for (let i = 0; i < str.length; i++) {
			const char = str.charCodeAt(i);
			hash = ((hash << 5) - hash) + char;
			hash = hash & hash; // Convert to 32bit integer
		}

		return Math.abs(hash).toString();
}

	/**
	* Returns milliseconds until a timeout
	*/
	static getTimeoutDuration(): number {
		return 30000; // 30 seconds
}

	/**
	* Returns a string representation of a duration in milliseconds
	*/
	static formatDuration(ms: number): string {
		const seconds = Math.floor((ms / 1000) % 60);
		const minutes = Math.floor((ms / (1000 * 60)) % 60);
		const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);
		const days = Math.floor(ms / (1000 * 60 * 60 * 24));

		const parts: string[] = [];

		if (days > 0) parts.push(`${days}d`);
		if (hours > 0) parts.push(`${hours}h`);
		if (minutes > 0) parts.push(`${minutes}m`);
		if (seconds > 0) parts.push(`${seconds}s`);

		return parts.length > 0 ? parts.join(' ') : '0s';
}

	/**
	* Returns a number rounded to the nearest integer
	*/
	static round(num: number): number {
		return Math.round(num);
}

	/**
	* Returns a number rounded down to the nearest integer
	*/
	static floor(num: number): number {
		return Math.floor(num);
}

	/**
	* Returns a number rounded up to the nearest integer
	*/
	static ceil(num: number): number {
		return Math.ceil(num);
}

	/**
	* Returns a number rounded to a specified number of decimal places
	*/
	static roundToDecimals(num: number, decimals: number): number {
		const factor = Math.pow(10, decimals);
		return Math.round(num * factor) / factor;
}

	/**
	* Returns a number formatted with thousands separators
	*/
	static formatNumber(num: number): string {
		return num.toLocaleString();
}

	/**
	* Returns a number formatted as a string with a specified precision
	*/
	static formatNumberAsString(num: number, decimals: number): string {
		return num.toFixed(decimals);
}

	/**
	* Returns a currency formatted string
	*/
	static formatCurrency(num: number, currency: string = 'USD'): string {
		return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(num);
}

	/**
	* Returns true if a value is not null, undefined, or empty string
	*/
	static hasValidValue(value: any): boolean {
		return value !== null && value !== undefined && value !== '';
}

	/**
	* Returns true if a record object has a valid id property
	*/
	static isValidRecord(record: any): boolean {
		return record !== null && record !== undefined && this.hasValidValue(record.id);
}

	/**
	 * Returns a moment-compatible UTC date. Accepts moment objects, Date, or string.
	 * When called with a moment object, returns the same moment (already UTC-aware).
	 */
	static getUTCDate(d: any = null): any {
		if (!d) return null;
		if (d && typeof d.isValid === 'function') {
			// already a moment-like object
			return d;
		}
		const date = new Date(d);
		return isNaN(date.getTime()) ? null : date;
}

	static versionCompare(left: string, right: string): number {
		const leftVal: any = left.match(/([0-9]+|[^0-9]+)/g);
		const rightVal: any = right.match(/([0-9]+|[^0-9]+)/g);
		for (;;) {
			let leftShift: any = leftVal.shift();
			let rightShift: any = rightVal.shift();
			if ((typeof leftShift === 'undefined') && (typeof rightShift === 'undefined')) {
				return 0;
			}
			if (typeof leftShift === 'undefined') leftShift = '';
			if (typeof rightShift === 'undefined') rightShift = '';
			const leftNum: number = parseInt(leftShift, 10);
			const rightNum: number = parseInt(rightShift, 10);
			if (isNaN(leftNum) || isNaN(rightNum)) {
				if (leftShift < rightShift) return -1;
				if (leftShift > rightShift) return 1;
			} else {
				if (leftNum < rightNum) return -1;
				if (leftNum > rightNum) return 1;
			}
		}
}

	static removeItem<T>(array: T[], item: T | any, field = 'id'): T[] {
		for (let li = 0; li < array.length; li++) {
			const element: T | any = array[li];
			if (element[field] === item[field]) {
				array.splice(li, 1);
				break;
			}
		}
		return array;
}

	static postToCache<T>(list: T[], record: T, idField = 'id', updateOnly = false): void {
		let found = false;
		idField = idField || 'id';
		for (let listItem of list) {
			type listItemKey = keyof typeof listItem;
			const idPropertyKey = idField as listItemKey;
			if (listItem[idPropertyKey] === record[idPropertyKey]) {
				listItem = record;
				found = true;
				break;
			}
		}
		if (!found && !updateOnly) {
			list.push(record);
		}
}

	static findRecord<T>(list: T[], id: any, idField = 'id', originalRecord = false): T {
		let record: T = null as any;
		if (list) {
			for (const listItem of list) {
				type listItemKey = keyof typeof listItem;
				const idPropertyKey = idField as listItemKey;
				if (listItem[idPropertyKey] === id) {
					if (originalRecord) {
						record = listItem;
					}
					break;
				}
			}
		}
		return record;
}

	static getRandomNumber(min: number, max: number): number {
		return Math.floor(Math.random() * (max - min + 1)) + min;
}

	static replaceTokens(path: string, tokens: any): string {
		if (tokens) {
			for (const prop in tokens) {
				// eslint-disable-next-line no-prototype-builtins
				if (tokens[prop] !== undefined && tokens.hasOwnProperty(prop)) {
					const token = ':' + prop;
					path = path.replace(token, tokens[prop]);
				}
			}
		}
		return path || '';
}

	/**
	 * Filters an array by matching a search term against the given fields (search form),
	 * or by predicate (existing form).
	 */
	static filter(array: any[], predicateOrTerm: any, fields?: string[], exactMatch?: boolean, reverse?: boolean): any[] {
		if (typeof predicateOrTerm === 'function') {
			return array.filter(predicateOrTerm);
		}
		const term: string = String(predicateOrTerm ?? '').toLowerCase();
		const results = array.filter((item: any) =>
			(fields ?? []).some((field: string) => {
				const val = String(item[field] ?? '').toLowerCase();
				return exactMatch ? val === term : val.includes(term);
			})
		);
		return reverse ? results.reverse() : results;
}

	static checkPassword(password: string): boolean {
		const passwordRE = /((?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*\W)){8,16}/;
		return !password || password.length === 0 || password.match(passwordRE) != null;
}

	static allValidValues(props: any[]): boolean {
		let isValid = true;
		for (const prop of props) {
			if (prop == null || typeof prop === 'undefined') {
				isValid = false;
				break;
			}
		}
		return isValid;
}

	static isValidString(prop: string): boolean {
		let isValid = true;
		if (prop == null || typeof prop === 'undefined' || prop.length === 0) {
			isValid = false;
		}
		return isValid;
}

	static hasValidValueIncludingNonEmptyArray(prop: any): boolean {
		let isValid = Utilities.hasValidValue(prop);
		if (isValid) {
			let zeroLength = false;
			const isArray: boolean = Array.isArray(prop);
			if (isArray) {
				zeroLength = prop.length === 0;
			}
			if (isArray && zeroLength) {
				isValid = false;
			}
		}
		return isValid;
}

	static _ToCamel(_scored: any): string {
		const buffer: string[] = [];
		const underScores: string[] = _scored.split('_');
		underScores.forEach(
			(term: string) => {
				buffer.push(term.charAt(0).toUpperCase());
				buffer.push(term.slice(1));
			}
		);
		return buffer.join('');
}

	static isInstanceOf = <T>(ctor: new (...args: any) => T) => (x: any): x is T => x instanceof ctor;

	static joinProperty(array: any[], property: string, delimiter = ','): string {
		let outStr = '';
		delimiter = delimiter || ',';
		for (const element of array) {
			if (outStr.length > 0) {
				outStr += delimiter;
			}
			outStr += element[property];
		}
		return outStr;
}

	static removePropertyToNewObject<O extends object, A extends keyof O>(object: O, attr: A): Omit<O, A> {
		const newObject = { ...object };
		if (attr in newObject) {
			delete newObject[attr];
		}
		return newObject;
}

	static removeProperty<O extends object, A extends keyof O>(object: O, attr: A): void {
		if (attr in object) {
			delete object[attr];
		}
}

	static appendIf(base: string, toAppend: string, whiteChar: string, condition: string): string {
		const truth = condition || toAppend;
		if (truth && whiteChar && base && base.length) {
			base += whiteChar;
		}
		return truth ? (base || '') + toAppend : base;
}

	static hasCacheExpired(lastPullTime: number, expireTerm = 0): boolean {
		const nowDate: Date = new Date();
		const nowTime: number = nowDate.getTime();
		let timeoutValue: number = 60 * 20 * 1000;
		if (expireTerm) {
			timeoutValue = expireTerm;
		}
		const elapsedTime: number = nowTime - lastPullTime;
		return (elapsedTime > timeoutValue);
}

	static postPotentiallyOverridingRecordToCache<T extends NonNullable<unknown>>(list: T[], record: T, idField = 'id', overridingIdField = 'overriding_id'): void {
		let found = false;
		for (let listItem of list) {
			type listItemKey = keyof typeof listItem;
			const overridingPropertyKey = overridingIdField as listItemKey;
			const idPropertyKey = idField as listItemKey;
			const fieldToCheck = (listItem[overridingPropertyKey]) ? overridingPropertyKey : idPropertyKey;
			if (listItem[fieldToCheck] === record[idPropertyKey]) {
				listItem = record;
				found = true;
				break;
			}
		}
		if (!found) {
			list.push(record);
		}
}

	static pullFromCache<T>(list: T[], recordId: string | number, idField: string): T {
		let returnedValue: T = null as any;
		let li = 0;
		for (const listItem of list) {
			type listItemKey = keyof typeof listItem;
			const idPropertyKey = idField as listItemKey;
			if (listItem[idPropertyKey] === recordId) {
				returnedValue = list.splice(li, 1)[0];
				break;
			}
			li++;
		}
		return returnedValue;
}

	static startsWith(sourceString: string, checkString: string): boolean {
		let starts = false;
		if (sourceString && checkString && sourceString.length > 0 && checkString.length > 0) {
			starts = (sourceString.substr(0, checkString.length) === checkString);
		}
		return starts;
}

	static copy<T>(source: T): T {
		return JSON.parse(JSON.stringify(source)) as T;
}

	static findLowestId<T>(list: T[], field: string): number {
		let lowestId = 0;
		let li = 0;
		for (const item of list) {
			type listItemKey = keyof typeof item;
			const fieldKey = field as listItemKey;
			if (li === 0) {
				lowestId = item[fieldKey] as number;
			} else if (item[fieldKey] as number < lowestId) {
				lowestId = item[fieldKey] as number;
			}
			li++;
		}
		return lowestId - 1;
}

	static parseDate(dateField: string): Date {
		let date: Date = new Date();
		const matches = dateField.match(/(\d+)/);
		if (matches && matches.length > 0) {
			const epoch = parseInt(matches[0], 10);
			date = new Date(epoch);
		}
		return date;
}

	static isDefined(itemToTest: any): boolean {
		return typeof itemToTest !== 'undefined';
}

	// the == (not ===) is the key here -- will match undefined as well
	static isUndefined(itemToTest: any): boolean {
		return itemToTest == null;
}

	static formatDateOutputShort(dateToFormat: Date): string {
		return `${ dateToFormat.getMonth() + 1}/${ dateToFormat.getDate()}/${ dateToFormat.getFullYear() }`;
}

	static formatDateOutputText(dateToFormat: Date, monthsArray: string[]): string {
		return `${ dateToFormat.getDate()}-${ monthsArray[dateToFormat.getMonth()] }-${ dateToFormat.getFullYear() }`;
}

	static formDateOutputFull(dateToFormat: Date): string {
		const fullHours = dateToFormat.getHours();
		const hours = fullHours > 12 ? fullHours - 12 : fullHours;
		const amPm = fullHours > 12 ? 'pm' : 'am';
		return `${ dateToFormat.getMonth() + 1}/${ dateToFormat.getDate()}/${ dateToFormat.getFullYear() % 2000 } ${ hours }:${ Utilities.padNumber(dateToFormat.getMinutes(), 2) } ${ amPm }`;
}

	static getFilenameFromPath(filePath: string): string {
		let filename: string = filePath;
		if (filePath) {
			const slashPos: number = filePath.lastIndexOf('/');
			if (slashPos >= 0) {
				filename = filePath.substring(slashPos + 1).toLowerCase();
			}
		}
		return filename;
}

	static readLocalJsonFile(filePath: File): Promise<string> {
		const deferred: Promise<string> = new Promise<string>(
			(resolve, reject) => {
				if (filePath && filePath.name.length > 3) {
					const reader = new FileReader();
					reader.onload = (file: any) => {
						const text: string = file.target.result;
						if (text && text.length > 0) {
							try {
								const json = JSON.parse(text);
								resolve(json);
							}
							catch (e) {
								reject(`Invalid json in ${filePath}: ${e}`);
							}
						} else {
							reject(`Couldn't read ${filePath}`);
						}
					};
					reader.readAsText(filePath);
				}
			}
		);
		return deferred;
}

	static extend(baseObj: any, extend: any) {
		for (const prop in extend) {
			// eslint-disable-next-line no-prototype-builtins
			if (extend[prop] !== undefined && extend.hasOwnProperty(prop)) {
				baseObj[prop] = extend[prop];
			}
		}
}

	static hashCode(data: any): number {
		if (typeof data === 'number') {
			return data as number;
		} else {
			return data.split('').reduce((a: any, b: any) => {
				a = ((a << 5) - a) + b.charCodeAt(0);
				return a & a;
			}, 0);
		}
}

	static timeDisplay(data: number | string): string {
		let display: string | null = null;
		if (typeof data !== 'string') {
			if (data >= 60) {
				display = `${Utilities.trimNumber(((data as number + .5) / 60), 1)}h`;
			} else {
				display = `${Utilities.trimNumber((data as number + .5), 0)}m`;
			}
		} else {
			display = data as string;
		}
		return display as string;
}

	static displayTimeMinutes(data: number | string, units = ' mins'): string | null {
		let display: string | null = null;
		if (typeof data !== 'string') {
			display = `${Utilities.trimNumber((data as number + .5) / 60, 0)} ${units}`;
		} else {
			display = data as string;
		}
		return display;
}

	static getEnumOptions(enumClass: any): any[] {
		const options: any[] = [];
		const enumValues: any[] = Object.keys(enumClass).map(key => enumClass[key]);
		const names = enumValues.filter(v => typeof v === 'string') as string[];
		const values = enumValues.filter(v => typeof v === 'number') as number[];
		for (let li = 0; li < names.length; li++) {
			options.push({ name: names[li], value: values[li], id: values[li], });
		}
		return options;
}

	static newGuid(): string {
		let guid = '';
		for (let li = 0; li < 32; li++) {
			if (li === 8 || li === 12 || li === 16 || li === 20) {
				guid = guid + '-';
			}
			guid = guid + Math.floor(Math.random() * 16).toString(16).toUpperCase();
		}
		return guid;
}

	static differences(base: NonNullable<unknown>, object: NonNullable<unknown>): any {
		let diff: any = false;

		if (typeof object == 'object') {
			for (const key in object) {
				type listItemKey = keyof typeof object;
				const fieldKey = key as listItemKey;

				// eslint-disable-next-line no-prototype-builtins
				if (base && base.hasOwnProperty(key)) {
					if (typeof object[fieldKey] != 'object' || !Utilities.hasValidValue(object[fieldKey])) {
						if (base[fieldKey] != object[fieldKey]) {
							if (diff == false){
								diff = {};
							}
							diff[key] = object[fieldKey];
						}
					} else {
						const subDiff = Utilities.differences(base[fieldKey], object[fieldKey]);
						if (subDiff !== false) {
							if (diff == false) {
								diff = {};
							}
							diff[key] = subDiff;
						}
					}
				} else if (Utilities.hasValidValue(object[fieldKey])) {
					if (Array.isArray(object[fieldKey])) {
						const fieldArray: any[] = object[fieldKey] as any[];
						if (fieldArray.length > 0) {
							if (diff == false){
								diff = {};
							}
							diff[key] = 'addition';
						}
					} else {
						if (diff == false){
							diff = {};
						}
						diff[key] = 'addition';
					}
				}
			}
		}
		if (typeof base == 'object') {
			type baseItemKey = keyof typeof object;
			for (const key in base) {
				const baseKey = key as baseItemKey;
				// eslint-disable-next-line no-prototype-builtins
				if (!object.hasOwnProperty(key) && Utilities.hasValidValue(baseKey)) {
					if (diff == false){
						diff = {};
					}
					diff[key] = 'subtraction';
				}
			}
		}
		return diff;
}

	static equalsIgnoreNulls(objectA: any, objectB: any): boolean {
		let keysA: string[] | null = null;

		if (!objectA && !objectB) {
			return true;
		}
		if (objectA === objectB) {
			return true;
		}
		if (typeof objectA !== typeof objectB) {
			return false;
		}
		if (objectA && objectB && typeof objectA === 'object') {
			if (objectA.constructor === objectB.constructor) {
				if (objectA instanceof Array) {
					if (objectA.length === objectB.length) {
						return objectA.every(
							(element, i) => {
								const value: boolean = Utilities.equalsIgnoreNulls(element, objectB[i]);
								return value;
							}
						);
					} else {
						return false;
					}
				} else if (objectA instanceof RegExp) {
					return (objectA.toString() === objectB.toString());
				} else {
					keysA = Object.keys(objectA);
					return keysA.every(
						(element) => {
							const value: boolean = Utilities.equalsIgnoreNulls(objectA[element], objectB[element]);
							return value;
						}
					);
				}
			} else {
				return false;
			}
		} else if (typeof objectA === 'function') {
			return (objectA.toString() === objectB.toString());
		} else {
			if (!objectA && !objectB) {
				return true;
			} else {
				return (objectA === objectB);
			}
		}
}

	static equals(objectA: any, objectB: any): boolean {
		let keysA: string[] | null = null;
		let keysB: string[] | null = null;

		if (!objectA && !objectB) {
			return true;
		}
		if (objectA === objectB) {
			return true;
		}
		if (typeof objectA !== typeof objectB) {
			return false;
		}
		if (objectA && objectB && typeof objectA === 'object') {
			if (objectA.constructor === objectB.constructor) {
				if (objectA instanceof Array) {
					if (objectA.length === objectB.length) {
						return objectA.every(
							(element, i) => {
								const value: boolean = Utilities.equals(element, objectB[i]);
								return value;
							}
						);
					} else {
						return false;
					}
				} else if (objectA instanceof RegExp) {
					return (objectA.toString() === objectB.toString());
				} else {
					keysA = Object.keys(objectA);
					keysB = Object.keys(objectB);
					if (keysA.length === keysB.length) {
						return keysA.every(
							(element) => {
								const value: boolean = Utilities.equals(objectA[element], objectB[element]);
								return value;
							}
						);
					} else {
						return false;
					}
				}
			} else {
				return false;
			}
		} else if (typeof objectA === 'function') {
			return (objectA.toString() === objectB.toString());
		} else {
			return (objectA === objectB);
		}
}

	static fixedRoundedDecimal(numToFix: number, places = 2): string {
		const fixedNumber: number = Utilities.roundDecimals(numToFix, places);
		let stringNumber: string = '' + fixedNumber;
		let numCurrentDecimals = 0;
		const decPos: number = stringNumber.indexOf('.');
		if (decPos < 0) {
			stringNumber += '.';
		} else {
			numCurrentDecimals = stringNumber.length - decPos - 1;
		}
		if (numCurrentDecimals < places) {
			for (; numCurrentDecimals < places; numCurrentDecimals++) {
				stringNumber = stringNumber + '0';
			}
		}
		return stringNumber;
}

	static trimNumber(numToTrim: number, places = 2): number {
		const maxPlaces: number = places;
		let stringNumber: string = '' + numToTrim;
		const decimalPos: number = stringNumber.indexOf('.');
		if (decimalPos >= 0) {
			const cropLength: number = decimalPos + maxPlaces + 1;
			stringNumber = stringNumber.substring(0, cropLength);
		}
		return parseFloat(stringNumber);
}

	static roundNumber(numToRound: number, targetIncrement = 1): number {
		const operand = (1 / targetIncrement);
		return (Math.round(numToRound * operand) / operand);
}

	static truncateDecimals(value: number, digits: number) {
		const step = Math.pow(10, digits || 0);
		const temp = Math.trunc(step * value);
		return temp / step;
}

	static truncDecimals(value: number, decimalPlaces = 0) {
		const step = Math.pow(10, decimalPlaces);
		return Math.trunc(step * value) / step;
}

	static roundDecimals(value: number, decimalPlaces = 0): number {
		const power = decimalPlaces < 0 ? 1 : decimalPlaces + 1;
		const step = Math.pow(10, power);
		const addend: number = 1 / step * 5;
		let temp = Math.trunc(step * (value + addend));
		temp = temp - (temp % 10);
		const result = temp / step;
		return result;
}

	static roundUpDecimals(value: number, decimalPlaces = 0): number {
		const power = decimalPlaces < 0 ? 1 : decimalPlaces + 1;
		const step = Math.pow(10, power);
		let temp = Math.ceil(step * (value));
		const modValue: number = (temp % 10);
		if (modValue) {
			temp = temp + 10 - modValue;
		}
		const result = temp / step;
		return result;
}

	static roundDownDecimals(value: number, decimalPlaces = 0): number {
		const power = decimalPlaces < 0 ? 1 : decimalPlaces + 1;
		const step = Math.pow(10, power);
		let temp = Math.ceil(step * (value));
		const modValue: number = (temp % 10);
		if (modValue) {
			temp = temp - 10 - modValue;
		}
		const result = temp / step;
		return result;
}

	static sorter<T>(records: T[], fields: string[]): T[] {
		const objectMap: ObjectMap = new ObjectMap();

		const sortDesc = (a: T, b: T, field: string): number => {
			type aFieldKeyType = keyof typeof a;
			type bFieldKeyType = keyof typeof b;
			const aFieldKey = field as aFieldKeyType;
			const bFieldKey = field as bFieldKeyType;

			let comp = 0;
			let nullClear = true;

			if (!a || a[aFieldKey] === null) {
				nullClear = false;
				comp = (b !== null) ? 0 : 1;
			} else if (!b || b[bFieldKey] === null) {
				nullClear = false;
				comp = -1;
			}

			if (nullClear) {
				let aValue: any = null;
				let bValue: any = null;

				if (field.indexOf('.') > 0) {
					aValue = objectMap.getValue(a, field).toLowerCase();
					bValue = objectMap.getValue(b, field.toLowerCase());
				} else if (typeof a[aFieldKey] === 'string') {
					aValue = a[aFieldKey];
					if (typeof aValue === 'string') {
						aValue = aValue.toLowerCase();
					}
					bValue = b[aFieldKey];
					if (typeof bValue === 'string') {
						bValue = bValue.toLowerCase();
					}
				} else {
					aValue = a[aFieldKey];
					bValue = b[bFieldKey];
				}
				if (aValue < bValue) {
					comp = 1;
				} else if (aValue > bValue) {
					comp = -1;
				}
			}
			return comp;
		};

		const sortAsc = (a: T, b: T, field: string) => {
			type aFieldKeyType = keyof typeof a;
			type bFieldKeyType = keyof typeof b;
			const aFieldKey = field as aFieldKeyType;
			const bFieldKey = field as bFieldKeyType;

			let comp = 0;
			let nullClear = true;

			if (!a || a[aFieldKey] === null) {
				nullClear = false;
				comp = (b !== null) ? 1 : 0;
			} else if (!b || b[bFieldKey] === null) {
				nullClear = false;
				comp = 1;
			}

			if (nullClear) {
				let aValue: any = null;
				let bValue: any = null;

				if (field.indexOf('.') > 0) {
					aValue = objectMap.getValue(a, field).toLowerCase();
					bValue = objectMap.getValue(b, field).toLowerCase();
				} else if (typeof a[aFieldKey] === 'string') {
					aValue = a[aFieldKey];
					if (typeof aValue === 'string') {
						aValue = aValue.toLowerCase();
					}
					bValue = b[aFieldKey];
					if (typeof bValue === 'string') {
						bValue = bValue.toLowerCase();
					}
				} else {
					aValue = a[aFieldKey];
					bValue = b[bFieldKey];
				}
				if (aValue < bValue) {
					comp = -1;
				} else if (aValue > bValue) {
					comp = 1;
				}
			}
			return comp;
		};

		return records.sort(
			(a: T, b: T) => {
				let comparison = 0;

				for (const field of fields) {
					let testValue = field;
					const firstChar: string = field.charAt(0);
					const desc: boolean = (firstChar === '-');
					if (firstChar === '-' || firstChar === '+') {
						testValue = field.substring(1);
					}
					if (desc) {
						comparison = sortDesc(a, b, testValue);
					} else {
						comparison = sortAsc(a, b, testValue);
					}
					if (comparison) {
						break;
					}
				}
				return comparison;
			}
		);
}

	static getImageSuffix(imagePath: string): string | null {
		let suffix: string | null = null;
		const perPos: number = imagePath.lastIndexOf('.');
		if (perPos > 2 && perPos > imagePath.length - 5) {
			suffix = imagePath.substring(perPos + 1).toLowerCase();
		}
		return suffix;
}

	static isImageSuffix(imagePath: string | null): boolean {
		let isImage = false;
		if (imagePath != null) {
			let suffix = Utilities.getImageSuffix(imagePath);
			if (!suffix) {
				suffix = imagePath;
			}
			if (suffix === 'png' || suffix === 'jpg' || suffix === 'jpeg' ||
					suffix === 'gif' || suffix === 'bmp') {
				isImage = true;
			}
		}
		return isImage;
}

	static convertImagePathToBase64(pathToImage: string): Promise<string> {
		const deferred = new Promise<string>(
			(resolve) => {
				const image: any = new Image();
				image.crossOrigin = 'Anonymous';
				image.onload = () => {
					let canvas: any = document.createElement('canvas');
					const ctx: any = canvas.getContext('2d');
					canvas.height = image.height;
					canvas.width = image.width;
					ctx.drawImage(image, 0, 0);
					const dataURL: any = canvas.toDataURL('image/' + Utilities.getImageSuffix(pathToImage));
					resolve(dataURL.replace(/^data:image\/(png|jpg|bmp|jpeg|gif);base64,/, ''));
					canvas = null;
				};
				image.src = pathToImage;
			}
		);
		return deferred;
}

	static determineImageType(imageData: number[]): any {
		const retval = {
			contentType: 'unknown',
			suffix: '',
			converted: false,
		};

		switch(imageData[0]) {
			case 1196314761:
				retval.contentType = 'image/png';
				retval.suffix = 'png';
				retval.converted = true;
				break;
			case 944130375:
				retval.contentType = 'image/gif';
				retval.suffix = 'gif';
				retval.converted = true;
				break;
			case 544099650:
				retval.contentType = 'image/bmp';
				retval.suffix = 'bmp';
				retval.converted = true;
				break;
			case -520103681:
				retval.contentType = 'image/jpg';
				retval.suffix = 'jpg';
				retval.converted = true;
				break;
			default:
				break;
		}
		return retval;
}

	static convertFileToBase64(jfile: any): Promise<string> {
		const deferred = new Promise<string>(
			(resolve) => {
				const reader = new FileReader();
				reader.onload = file => {
					const contents: any = file.target;
					resolve(btoa(contents.result));
				};
				reader.readAsBinaryString(jfile);
			}
		);
		return deferred;
}

	static base64ToArray(base64: string): ArrayBuffer {
		const binaryString = window.atob(base64);
		const binaryLength = binaryString.length;
		const bytes = new Uint8Array(binaryLength);
		for (let li = 0; li < binaryLength; li++) {
			bytes[li] = binaryString.charCodeAt(li);
		}
		return bytes.buffer;
}

	static keyStr = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';

	static encodeBase64(decoded: string): string {
		let output = '';
		let chr1, chr2, chr3, enc1, enc2, enc3, enc4;
		let li = 0;

		const utf8Decoded = Utilities.utf8Encode(decoded);

		while (li < utf8Decoded.length) {
			chr1 = utf8Decoded.charCodeAt(li++);
			chr2 = utf8Decoded.charCodeAt(li++);
			chr3 = utf8Decoded.charCodeAt(li++);

			enc1 = chr1 >> 2;
			enc2 = ((chr1 & 3) << 4) | (chr2 >> 4);
			enc3 = ((chr2 & 15) << 2) | (chr3 >> 6);
			enc4 = chr3 & 63;

			if (isNaN(chr2)) {
				enc3 = enc4 = 64;
			} else if (isNaN(chr3)) {
				enc4 = 64;
			}
			output = output +
				Utilities.keyStr.charAt(enc1) + Utilities.keyStr.charAt(enc2) + Utilities.keyStr.charAt(enc3) + Utilities.keyStr.charAt(enc4);
		}
		return output;
}

	static decodeBase64(encoded: string): string {
		let l = 0;
		let b = 0;
		let restoredString = '';

		const encodedStringArray: string[] = encoded.split('');
		const possibleChars: string[] = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/='.split('');

		for (const idx in encodedStringArray) {
			// eslint-disable-next-line no-prototype-builtins
			if (encodedStringArray.hasOwnProperty(idx)) {
				b = (b << 6) + possibleChars.indexOf(encodedStringArray[idx]);
				l += 6;
				while (l >= 8) {
					restoredString += String.fromCharCode((b >>> (l -= 8)) & 0xff);
				}
			}
		}
		return restoredString;
}

	static utf8Encode(decoded: string) {
		const strippedString = decoded.replace(/\r\n/g, '\n');
		let utftext = '';

		for (let n = 0; n < strippedString.length; n++) {
			const c = strippedString.charCodeAt(n);
			if (c < 128) {
				utftext += String.fromCharCode(c);
			} else if((c > 127) && (c < 2048)) {
				utftext += String.fromCharCode((c >> 6) | 192);
				utftext += String.fromCharCode((c & 63) | 128);
			} else {
				utftext += String.fromCharCode((c >> 12) | 224);
				utftext += String.fromCharCode(((c >> 6) & 63) | 128);
				utftext += String.fromCharCode((c & 63) | 128);
			}
		}
		return utftext;
}

	static onMobile(): boolean {
		return window.matchMedia('only screen and (max-width: 899px)').matches;
}

	static lowerCaseFirstChar(value: string): string {
		const valueArray: string[] = value.split('');
		valueArray[0] = valueArray[0].toLowerCase();
		return valueArray.join('');
}

	static compressJson(data: any): number[] {
		const dictionary: any = {};
		const stringData = JSON.stringify(data);

		for (let li = 0; li < 256; li++) {
			dictionary[String.fromCharCode(li)] = li;
		}

		let word = '';
		const result: number[] = [];
		let dictSize = 256;

		for (let li = 0, len = stringData.length; li < len; li++) {
			const curChar = stringData[li];
			const joinedWord = word + curChar;

			// eslint-disable-next-line no-prototype-builtins
			if (dictionary.hasOwnProperty(joinedWord)) {
				word = joinedWord;
			} else {
				result.push(dictionary[word]);
				dictionary[joinedWord] = dictSize++;
				word = curChar;
			}
		}
		if (word !== '') {
			result.push(dictionary[word]);
		}
		return result;
}

	static uncompressJson(compressedData: number[]): any {
		const dictionary: string[] = [];

		for (let li = 0; li < 256; li++) {
			dictionary.push(String.fromCharCode(li));
		}

		let word = String.fromCharCode(compressedData[0]);
		let result = word;
		let dictSize = 256;

		for (let li = 1, len = compressedData.length; li < len; li++) {
			const curNumber = compressedData[li];
			let entry = '';

			if (dictionary[curNumber] !== undefined) {
				entry = dictionary[curNumber];
			} else {
				if (curNumber === dictSize) {
					entry = word + word[0];
				} else {
					throw 'Error in processing';
				}
			}

			result += entry;
			dictionary[dictSize++] = word + entry[0];
			word = entry;
		}
		let json: any = {};
		try {
			json = JSON.parse(result);
		}
		catch (ex: any) {
			console.log(ex);
		}
		return json;
}

}
